import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import RecordRTC from 'recordrtc';

const SEGMENT_SECONDS = 240; // ~7.7MB WAV @ 16kHz mono — well under the backend 15MB chunk cap
const MIN_CHUNK_BYTES = 1000; // matches backend validateChunkUpload; skip sub-second tails

@observer
class ConsultationRecorder extends Component {
    constructor(props) {
        super(props);
        this.state = {
            consentChecked: false,
            status: 'idle', // idle | preparing | recording | uploading | generating | done | empty | error
            recordingTime: 0,
            pendingUploads: 0,
            failedChunks: 0,
            error: null,
            availableDevices: [],
            selectedDeviceId: null,
            showDeviceSelector: false,
        };
        this.recordingUuidv4 = null;
        this.seq = 0;
        this.uploadQueue = Promise.resolve();
        this.failedBlobs = [];
        this.stream = null;
        this.recorder = null;
        this.segmentTimer = null;
        this.recordingTimer = null;
        this.segmentStartedAt = 0;
        this.audioContext = null;
        this.analyser = null;
        this.stopping = false;
        this.harvestPromise = null;
        this.unmounted = false;
    }

    // Guards setState against firing after unmount — used only by the background
    // upload path (enqueueUpload/uploadChunk/retryFailedUploads) which can still be
    // in flight after componentWillUnmount triggers a best-effort final harvest.
    safeSetState = (update) => {
        if (!this.unmounted) {
            this.setState(update);
        }
    }

    componentDidMount() {
        this.loadDevices();
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopping = true;
        this.clearTimers();
        this.removeUnloadGuard();
        if (this.recorder) {
            // Best-effort background upload of the in-progress segment — do NOT
            // await (componentWillUnmount can't be async) and do NOT call generate().
            this.harvestSegment(false);
        }
        this.releaseStream();
    }

    loadDevices = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === 'audioinput');
            const savedDeviceId = localStorage.getItem('audioRecorder_selectedDeviceId');
            let selectedDeviceId = audioInputs[0]?.deviceId || null;
            if (savedDeviceId && audioInputs.some(d => d.deviceId === savedDeviceId)) {
                selectedDeviceId = savedDeviceId;
            }
            this.setState({ availableDevices: audioInputs, selectedDeviceId });
        } catch (error) {
            console.error('Error loading devices:', error);
        }
    }

    getAudioStream = async () => {
        const { selectedDeviceId } = this.state;
        const audioConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
        };
        if (selectedDeviceId) {
            audioConstraints.deviceId = { exact: selectedDeviceId };
        }
        return navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    }

    checkMicHasSignal = (analyser) => {
        const dataArray = new Uint8Array(analyser.fftSize);
        analyser.getByteTimeDomainData(dataArray);
        let maxDeviation = 0;
        for (let i = 0; i < dataArray.length; i += 1) {
            const deviation = Math.abs(dataArray[i] - 128);
            if (deviation > maxDeviation) maxDeviation = deviation;
        }
        return maxDeviation > 1;
    }

    waitForMicSignal = (analyser, timeoutMs = 2000) => new Promise((resolve) => {
        const startTime = Date.now();
        const check = () => {
            if (this.checkMicHasSignal(analyser)) {
                resolve(true);
            } else if (Date.now() - startTime > timeoutMs) {
                resolve(false);
            } else {
                setTimeout(check, 50);
            }
        };
        check();
    })

    setupAnalyser = async (stream) => {
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
            this.analyser = null;
        }
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }
        const sourceNode = this.audioContext.createMediaStreamSource(stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 512;
        sourceNode.connect(this.analyser);
        return this.analyser;
    }

    onBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = '';
    }

    addUnloadGuard = () => window.addEventListener('beforeunload', this.onBeforeUnload)

    removeUnloadGuard = () => window.removeEventListener('beforeunload', this.onBeforeUnload)

    clearTimers = () => {
        if (this.segmentTimer) {
            clearTimeout(this.segmentTimer);
            this.segmentTimer = null;
        }
        if (this.recordingTimer) {
            clearInterval(this.recordingTimer);
            this.recordingTimer = null;
        }
    }

    releaseStream = () => {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
            this.analyser = null;
        }
    }

    start = async () => {
        this.stopping = false;
        this.harvestPromise = null;
        const { consentChecked } = this.state;
        if (!consentChecked) {
            return;
        }
        try {
            this.setState({ status: 'preparing', error: null, recordingTime: 0, failedChunks: 0 });
            this.failedBlobs = [];
            this.seq = 0;
            this.uploadQueue = Promise.resolve();

            const { journalStore } = this.props.stores;
            const { newJournal = {} } = journalStore;
            const response = await util.fetchApi('/api/consultationrecordings', { method: 'POST' }, {
                journal: newJournal.id || 0,
                journalUuidv4: newJournal.uuidv4 || '',
            });
            if (response?.status !== 201 || !response?.data?.uuidv4) {
                throw new Error(response?.message || 'Could not start recording');
            }
            this.recordingUuidv4 = response.data.uuidv4;

            // macOS: the first getUserMedia after page load can return a dead
            // stream — detect and retry once (same fix as audioRecorder.js).
            // this.stream is assigned immediately after each grant (not just once at
            // the end) so a throw anywhere below always leaves releaseStream() with a
            // live stream to stop — otherwise a granted MediaStream can leak.
            let stream = await this.getAudioStream();
            this.stream = stream;
            await this.loadDevices();
            let analyser = await this.setupAnalyser(stream);
            const hasSignal = await this.waitForMicSignal(analyser, 500);
            if (!hasSignal) {
                stream.getTracks().forEach(track => track.stop());
                stream = await this.getAudioStream();
                this.stream = stream;
                analyser = await this.setupAnalyser(stream);
                await this.waitForMicSignal(analyser, 1500);
            }

            this.startSegment();
            this.addUnloadGuard();
            this.setState({ status: 'recording' });
            this.recordingTimer = setInterval(() => {
                this.setState({ recordingTime: this.state.recordingTime + 1 });
            }, 1000);
            this.segmentTimer = setTimeout(this.rotateSegment, SEGMENT_SECONDS * 1000);
        } catch (error) {
            console.error('Error starting consultation recording:', error);
            this.releaseStream();
            this.setState({ status: 'error', error: error.message || 'Could not access microphone.' });
        }
    }

    startSegment = () => {
        this.recorder = new RecordRTC(this.stream, {
            type: 'audio',
            mimeType: 'audio/wav',
            recorderType: RecordRTC.StereoAudioRecorder,
            desiredSampRate: 16000,
            numberOfAudioChannels: 1,
        });
        this.recorder.startRecording();
        this.segmentStartedAt = Date.now();
    }

    // Captures and nulls out this.recorder BEFORE any async boundary so a concurrent
    // call (stop() racing an in-flight rotateSegment) can never double-stop/destroy
    // the same recorder instance — a second caller just sees this.recorder === null
    // and resolves immediately. The in-flight promise is also stashed on
    // this.harvestPromise so stop() can await exactly the harvest already running.
    harvestSegment = (restart) => {
        const recorder = this.recorder;
        if (!recorder) {
            return Promise.resolve();
        }
        this.recorder = null;
        const seconds = Math.round((Date.now() - this.segmentStartedAt) / 1000);
        const promise = new Promise((resolve) => {
            recorder.stopRecording(() => {
                const blob = recorder.getBlob();
                recorder.destroy();
                if (blob && blob.size >= MIN_CHUNK_BYTES) {
                    this.enqueueUpload(blob, this.seq, seconds);
                    this.seq += 1;
                }
                if (restart && !this.stopping) {
                    this.startSegment();
                }
                resolve();
            });
        });
        this.harvestPromise = promise;
        return promise;
    }

    rotateSegment = async () => {
        await this.harvestSegment(true);
        if (!this.stopping) {
            this.segmentTimer = setTimeout(this.rotateSegment, SEGMENT_SECONDS * 1000);
        }
    }

    enqueueUpload = (blob, seq, seconds) => {
        this.safeSetState(prev => ({ pendingUploads: prev.pendingUploads + 1 }));
        // Serial queue: the backend does read-modify-write on chunks — uploads
        // must never overlap.
        this.uploadQueue = this.uploadQueue
            .then(() => this.uploadChunk(blob, seq, seconds))
            .then(() => this.safeSetState(prev => ({ pendingUploads: prev.pendingUploads - 1 })));
    }

    uploadChunk = async (blob, seq, seconds, attempt = 1) => {
        try {
            const formData = new FormData();
            formData.append('audio', blob, `segment-${seq}.wav`);
            formData.append('uuidv4', this.recordingUuidv4);
            formData.append('seq', seq);
            formData.append('seconds', seconds);
            const token = util.getJwtToken();
            const apiServer = util.getApiServer();
            const fetchResponse = await fetch(`${apiServer}/api/consultationrecordings/chunk`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            const response = await fetchResponse.json();
            if (response.status !== 200) {
                throw new Error(response.message || 'Upload failed');
            }
        } catch (error) {
            console.error(`Chunk ${seq} upload failed (attempt ${attempt}):`, error);
            if (attempt < 2) {
                return this.uploadChunk(blob, seq, seconds, attempt + 1);
            }
            this.failedBlobs.push({ blob, seq, seconds });
            this.safeSetState({ failedChunks: this.failedBlobs.length });
        }
    }

    retryFailedUploads = async () => {
        const failed = this.failedBlobs;
        this.failedBlobs = [];
        this.safeSetState({ failedChunks: 0 });
        for (let i = 0; i < failed.length; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await this.uploadChunk(failed[i].blob, failed[i].seq, failed[i].seconds);
        }
        this.safeSetState({ failedChunks: this.failedBlobs.length });
    }

    stop = async () => {
        this.stopping = true;
        this.clearTimers();
        this.safeSetState({ status: 'uploading' });
        // Await any harvest already in flight (a rotateSegment mid-swap) first, so its
        // chunk is enqueued before we harvest the (now current) final segment and wait
        // on the upload queue below.
        if (this.harvestPromise) {
            await this.harvestPromise;
        }
        await this.harvestSegment(false);
        this.releaseStream();
        await this.uploadQueue;
        if (this.failedBlobs.length > 0) {
            await this.retryFailedUploads();
        }
        if (this.failedBlobs.length > 0) {
            this.removeUnloadGuard();
            this.safeSetState({ status: 'error', error: 'Noen lydsegmenter ble ikke lastet opp.' });
            return;
        }
        await this.generate();
    }

    generate = async () => {
        if (!this.recordingUuidv4) {
            this.removeUnloadGuard();
            this.safeSetState({ status: 'error', error: 'Kunne ikke generere journalforslag.' });
            return;
        }
        const { journalStore } = this.props.stores;
        this.safeSetState({ status: 'generating', error: null });
        const data = await journalStore.queryAiFromConsultation(this.recordingUuidv4);
        this.removeUnloadGuard();
        if (!data?.output_text) {
            this.safeSetState({ status: 'error', error: 'Kunne ikke generere journalforslag.' });
            return;
        }
        // The backend returns hasSuggestions=false when the model found nothing
        // journal-relevant (e.g. a short mic test): say so instead of "Forslag klare".
        if (data.hasSuggestions === false) {
            this.safeSetState({ status: 'empty' });
            return;
        }
        this.safeSetState({ status: 'done' });
    }

    retry = async () => {
        if (!this.recordingUuidv4) {
            return this.start();
        }
        this.safeSetState({ status: 'uploading', error: null });
        await this.retryFailedUploads();
        if (this.failedBlobs.length > 0) {
            this.safeSetState({ status: 'error', error: 'Noen lydsegmenter ble ikke lastet opp.' });
            return;
        }
        await this.generate();
    }

    reset = () => {
        this.recordingUuidv4 = null;
        this.seq = 0;
        this.failedBlobs = [];
        this.uploadQueue = Promise.resolve();
        this.removeUnloadGuard();
        this.setState({
            consentChecked: false,
            status: 'idle',
            recordingTime: 0,
            pendingUploads: 0,
            failedChunks: 0,
            error: null,
        });
    }

    formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${String(secs).padStart(2, '0')}`;
    }

    render() {
        const {
            consentChecked, status, recordingTime, pendingUploads, error,
            availableDevices, selectedDeviceId, showDeviceSelector,
        } = this.state;

        return (
            <div class='card mb-2'>
                <div class='card-body py-2'>
                    <div class='d-flex align-items-center gap-2 flex-wrap'>
                        <i class='fa-duotone fa-waveform-lines' />
                        <strong><Text id='dyrejournal.consultation.title'>Consultation Mode</Text></strong>

                        {status === 'idle' && <>
                            <div class='form-check ms-2'>
                                <input
                                    class='form-check-input'
                                    type='checkbox'
                                    id='consultationConsent'
                                    checked={consentChecked}
                                    onInput={e => this.setState({ consentChecked: e.target.checked })}
                                />
                                <label class='form-check-label' for='consultationConsent'>
                                    <Text id='dyrejournal.consultation.consent'>The animal owner has been informed that the consultation is being recorded</Text>
                                </label>
                            </div>
                            <button
                                type='button'
                                class='btn btn-outline-primary rounded-pill'
                                onClick={this.start}
                                disabled={!consentChecked}
                                style={!consentChecked ? 'pointer-events: auto;' : ''}
                            >
                                <i class='fa-solid fa-microphone me-1' />
                                <Text id='dyrejournal.consultation.start'>Start recording</Text>
                            </button>
                            {availableDevices.length > 1 && <div class='dropdown'>
                                <button
                                    type='button'
                                    class='btn btn-outline-secondary rounded-pill'
                                    onClick={() => this.setState({ showDeviceSelector: !showDeviceSelector })}
                                >
                                    <i class='fa-solid fa-cog' />
                                </button>
                                {showDeviceSelector && <div class='dropdown-menu show' style={{ position: 'absolute', zIndex: 1000 }}>
                                    {availableDevices.map((device, index) => (
                                        <button
                                            key={device.deviceId}
                                            type='button'
                                            class={`dropdown-item ${device.deviceId === selectedDeviceId ? 'active' : ''}`}
                                            onClick={() => {
                                                localStorage.setItem('audioRecorder_selectedDeviceId', device.deviceId);
                                                this.setState({ selectedDeviceId: device.deviceId, showDeviceSelector: false });
                                            }}
                                        >
                                            {device.label || `Microphone ${index + 1}`}
                                        </button>
                                    ))}
                                </div>}
                            </div>}
                        </>}

                        {status === 'preparing' && <span class='text-body-secondary'>
                            <i class='fa-solid fa-spinner fa-spin me-1' />
                            <Text id='dyrejournal.consultation.preparing'>Preparing microphone...</Text>
                        </span>}

                        {status === 'recording' && <>
                            <span class='badge bg-danger'>
                                <i class='fa-solid fa-circle fa-beat me-1' />
                                <Text id='dyrejournal.consultation.recording'>Recording</Text>
                            </span>
                            <span class='badge bg-light text-dark'>{this.formatTime(recordingTime)}</span>
                            {pendingUploads > 0 && <small class='text-body-secondary'>
                                <i class='fa-solid fa-cloud-arrow-up me-1' />
                                {pendingUploads}
                            </small>}
                            <button type='button' class='btn btn-danger rounded-pill' onClick={this.stop}>
                                <i class='fa-solid fa-stop me-1' />
                                <Text id='dyrejournal.consultation.stop'>Finish and draft journal</Text>
                            </button>
                        </>}

                        {status === 'uploading' && <span class='text-body-secondary'>
                            <i class='fa-solid fa-spinner fa-spin me-1' />
                            <Text id='dyrejournal.consultation.uploading'>Uploading and transcribing...</Text>
                        </span>}

                        {status === 'generating' && <span class='text-body-secondary'>
                            <i class='fa-solid fa-spinner fa-spin me-1' />
                            <Text id='dyrejournal.consultation.generating'>Drafting journal suggestions...</Text>
                        </span>}

                        {status === 'done' && <>
                            <span class='text-success'>
                                <i class='fa-solid fa-check me-1' />
                                <Text id='dyrejournal.consultation.done'>Suggestions ready, see the journal fields below</Text>
                            </span>
                            <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.reset}>
                                <Text id='dyrejournal.consultation.newRecording'>New recording</Text>
                            </button>
                        </>}

                        {status === 'empty' && <>
                            <span class='text-warning'>
                                <i class='fa-solid fa-triangle-exclamation me-1' />
                                <Text id='dyrejournal.consultation.empty'>No journal-relevant content was found in the recording</Text>
                            </span>
                            <button type='button' class='btn btn-sm btn-outline-primary rounded-pill' onClick={this.retry}>
                                <i class='fa-solid fa-rotate-right me-1' />
                                <Text id='dyrejournal.consultation.retry'>Try again</Text>
                            </button>
                            <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.reset}>
                                <Text id='dyrejournal.consultation.newRecording'>New recording</Text>
                            </button>
                        </>}

                        {status === 'error' && <>
                            <small class='text-danger'>{error}</small>
                            <button type='button' class='btn btn-sm btn-outline-primary rounded-pill' onClick={this.retry}>
                                <i class='fa-solid fa-rotate-right me-1' />
                                <Text id='dyrejournal.consultation.retry'>Try again</Text>
                            </button>
                            <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.reset}>
                                <Text id='dyrejournal.consultation.newRecording'>New recording</Text>
                            </button>
                        </>}
                    </div>
                </div>
            </div>
        );
    }
}

export default ConsultationRecorder;
